import { createServerFn } from "@tanstack/react-start"
import { createResponse, getResponse, updateResponseProfile, type SurveyResponse } from "~/lib/db"
import { computeAllScores } from "~/lib/scoring"
import { getSanityClient, getPreviewClient } from "~/lib/sanity"
import {
  LANDING_PAGE_QUERY,
  PROFILE_BY_SLUG_QUERY,
  ALL_PROFILES_QUERY,
  SHARED_CONTENT_QUERY,
} from "~/lib/sanity-queries"
import type { KeyModel } from "~/data/profiles"
import { mapSanityProfile } from "~/lib/sanity-mappers"
import { sendReportEmail } from "~/lib/email/send-report-email"
import { captureHandledException, withCapturedException } from "~/lib/handled-errors"
import { getComputedProfileIds } from "./computed-profiles"

export { getComputedProfileIds }

export function shouldSendReportEmails(options: {
  useLocalDb: boolean
  resendApiKey?: string
}): boolean {
  return !options.useLocalDb && Boolean(options.resendApiKey)
}

export function runServerFn<T>(action: string, fn: () => Promise<T>): Promise<T> {
  return withCapturedException(
    {
      feature: "server-fns",
      action,
      layer: "server",
    },
    fn,
  )
}

export const submitSurvey = createServerFn({ method: "POST" })
  .inputValidator((d: { answers: Record<number, number>; demographics: Record<string, string>; baseUrl: string }) => d)
  .handler(async ({ data }) =>
    runServerFn("submitSurvey", async () => {
      const scores = computeAllScores(data.answers)
      const id = crypto.randomUUID()
      const computedProfiles = getComputedProfileIds(scores)

      const response: SurveyResponse = {
        id,
        submittedAt: new Date().toISOString(),
        answers: data.answers,
        identityScore: scores.identity,
        purposeScore: scores.purpose,
        communityScore: scores.community,
        demographics: data.demographics,
        selectedProfile: computedProfiles.length === 1 ? computedProfiles[0] : undefined,
        computedProfiles,
        computedLink: `${data.baseUrl}/report/${id}`,
      }
      await createResponse(response)

      const email = data.demographics.email
      const canSendEmail = shouldSendReportEmails({
        useLocalDb: process.env.USE_LOCAL_DB === "true",
        resendApiKey: process.env.RESEND_API_KEY,
      })

      if (email && computedProfiles.length === 1 && canSendEmail) {
        void sendReportEmail({
          to: email,
          firstName: data.demographics.first_name || "",
          profileSlug: computedProfiles[0],
          reportUrl: `${data.baseUrl}/report/${id}`,
        })
          .then((resendId) => console.log(`[email] sent ${id} → ${resendId}`))
          .catch(async (err) => {
            console.error(`[email] failed ${id}:`, err.message)
            await captureHandledException(err, {
              feature: "email",
              action: "send-report-email",
              layer: "server",
              fallback: "log-only",
              extra: { reportId: id, profileSlug: computedProfiles[0] },
            })
          })
      }

      return { id }
    }),
  )

export const getReport = createServerFn({ method: "GET" })
  .inputValidator((d: string) => d)
  .handler(async ({ data: id }) =>
    runServerFn("getReport", async () => {
      const response = await getResponse(id)
      if (!response) {
        throw new Error("Report not found")
      }
      return {
        id: response.id,
        name: [response.demographics?.first_name, response.demographics?.last_name]
          .filter(Boolean)
          .join(" ") || "",
        identityScore: response.identityScore,
        purposeScore: response.purposeScore,
        communityScore: response.communityScore,
        selectedProfile: response.selectedProfile || null,
      }
    }),
  )

export const saveProfileSelection = createServerFn({ method: "POST" })
  .inputValidator((d: { reportId: string; profileId: string }) => d)
  .handler(async ({ data }) =>
    runServerFn("saveProfileSelection", async () => {
      const response = await getResponse(data.reportId)
      const alreadySelected = response?.selectedProfile
      await updateResponseProfile(data.reportId, data.profileId)
      const email = response?.demographics?.email
      const canSendEmail = shouldSendReportEmails({
        useLocalDb: process.env.USE_LOCAL_DB === "true",
        resendApiKey: process.env.RESEND_API_KEY,
      })

      if (
        email &&
        !alreadySelected &&
        response.computedProfiles &&
        response.computedProfiles.length > 1 &&
        canSendEmail
      ) {
        void sendReportEmail({
          to: email,
          firstName: response.demographics.first_name || "",
          profileSlug: data.profileId,
          reportUrl: response.computedLink || "",
          isDualProfile: true,
        })
          .then((resendId) => console.log(`[email] sent ${data.reportId} → ${resendId}`))
          .catch(async (err) => {
            console.error(`[email] failed ${data.reportId}:`, err.message)
            await captureHandledException(err, {
              feature: "email",
              action: "send-report-email",
              layer: "server",
              fallback: "log-only",
              extra: { reportId: data.reportId, profileSlug: data.profileId, isDualProfile: true },
            })
          })
      }

      return { ok: true }
    }),
  )

// --- Sanity data fetchers ---

function clientFor(preview?: boolean) {
  return preview ? getPreviewClient() : getSanityClient()
}

export const getLandingPage = createServerFn({ method: "GET" })
  .inputValidator((d: { preview?: boolean }) => d)
  .handler(async ({ data }) =>
    runServerFn("getLandingPage", async () => {
      return await clientFor(data.preview).fetch(LANDING_PAGE_QUERY)
    }),
  )

export const getProfileBySlug = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string; preview?: boolean }) => d)
  .handler(async ({ data }) =>
    runServerFn("getProfileBySlug", async () => {
      const doc = await clientFor(data.preview).fetch(PROFILE_BY_SLUG_QUERY, { slug: data.slug })
      if (!doc) throw new Error("Profile not found")
      return mapSanityProfile(doc)
    }),
  )

export const getAllSanityProfiles = createServerFn({ method: "GET" })
  .inputValidator((d: { preview?: boolean }) => d)
  .handler(async ({ data }) =>
    runServerFn("getAllSanityProfiles", async () => {
      const docs = await clientFor(data.preview).fetch(ALL_PROFILES_QUERY)
      return (docs as Record<string, unknown>[]).map(mapSanityProfile)
    }),
  )

export const getSharedContent = createServerFn({ method: "GET" })
  .inputValidator((d: { preview?: boolean }) => d)
  .handler(async ({ data }) =>
    runServerFn("getSharedContent", async () => {
      const doc = await clientFor(data.preview).fetch(SHARED_CONTENT_QUERY)
      if (!doc) return null
      const d = doc as Record<string, unknown>
      return {
        keyModel: {
          name: (d.modelJesusName as string) || "Jesus",
          content: (d.modelJesusContent as string) || "",
        } satisfies KeyModel,
        multipleProfileMessage: (d.multipleProfileMessage as string) || "",
      }
    }),
  )
